import type { ComponentType } from 'react';
import {
  IconBriefcase,
  IconBuilding,
  IconBuilding2,
  IconCalendarCheck,
  IconCalendarOff,
  IconCircleHelp,
  IconClipboardList,
  IconFileText,
  IconGauge,
  IconGlobe,
  IconLandPlot,
  IconLayers,
  IconListChecks,
  IconMail,
  IconMap,
  IconMapPin,
  IconMapPinned,
  IconNetwork,
  IconSlidersHorizontal,
  IconShieldCheck,
  IconStore,
  IconTags,
  IconUserRound,
  IconUsers,
  type IconProps,
} from '../icons';
import { Permission } from '../auth/permissions';

export interface NavItem {
  to: string;
  /** Dictionary key. Nothing in the menu is a literal — the shell is translated too. */
  labelKey: string;
  icon: ComponentType<IconProps>;
  /**
   * Hidden unless the token carries this permission. The API still checks it.
   *
   * <p>A list means <b>any one of them</b>, for the few screens two audiences share: the
   * actions screen is an owner's list of work and an approver's queue, and gating it on the
   * owner's permission alone left approvers with no way to reach the thing they approve.</p>
   */
  permission?: string | readonly string[];
}

/**
 * Whether a menu item is shown to somebody who `allows` these permissions.
 *
 * <p>One place, because the menu is read twice — once to draw it and once to decide where
 * signing in lands — and the two disagreeing is how a person lands on a screen that is not
 * in their menu.</p>
 */
export function maySee(item: NavItem, allows: (permission?: string) => boolean): boolean {
  if (item.permission === undefined) return true;

  return typeof item.permission === 'string'
    ? allows(item.permission)
    : item.permission.some((one) => allows(one));
}

export interface NavGroup {
  id: string;
  labelKey: string;
  items: NavItem[];
}

/**
 * The menu, in the order somebody setting the product up has to work.
 *
 * <p>Geography before the organisation because a region sits in a country, and both before
 * locations because a location needs a type and a region. Working down the list is the
 * setup.</p>
 *
 * <p>Each item names the permission its screen's endpoints require, so the menu cannot
 * offer a page that answers 403. The permission on each item is the one the matching
 * endpoint group asks for — which is why location types and departments carry
 * `organisation.manage` even though they are read as definitions.</p>
 */
/**
 * Where to send somebody who has just signed in, or who asked for "/".
 *
 * <p>Read off the menu rather than written down, because a written-down answer goes stale
 * the moment somebody's permissions differ from the person who wrote it. A quality manager
 * has no `audits.conduct` and used to land on Denetimler — a screen whose "start an audit"
 * call answers 403, so the first thing they saw after signing in was an error.</p>
 *
 * <p>Falls back to the audits screen when nothing matches: somebody with no permission at
 * all has a problem the router cannot solve, and sending them to a screen that explains
 * itself beats sending them nowhere.</p>
 */
export function landingPath(allows: (permission?: string) => boolean): string {
  for (const group of NAVIGATION) {
    for (const item of group.items) {
      if (maySee(item, allows)) return item.to;
    }
  }

  return '/denetimler';
}

export const NAVIGATION: NavGroup[] = [
  {
    id: 'field',
    labelKey: 'app.field',
    items: [
      { to: '/gorevlerim', labelKey: 'nav.tasks', icon: IconCalendarCheck, permission: Permission.ConductAudits },
      { to: '/denetimler', labelKey: 'nav.audits', icon: IconClipboardList, permission: Permission.ConductAudits },
      {
        to: '/aksiyonlar',
        labelKey: 'nav.actions',
        icon: IconListChecks,
        // Both audiences: the owner answers, the approver decides. The screen already has a
        // tab for each.
        permission: [Permission.RespondToActions, Permission.ApproveActions],
      },
      // The planning screen, not the task list — that is Görevlerim, behind audits.conduct.
      // It used to be behind audits.read, so it stood in an auditor's menu offering to plan
      // other people's months. Under Saha, not Raporlar: planning is work, and it sat among
      // reports nobody looks in to plan (field test, 2 November).
      { to: '/denetim-plani', labelKey: 'nav.plans', icon: IconCalendarCheck, permission: Permission.ManagePlans },
    ],
  },
  {
    id: 'reports',
    labelKey: 'app.reports',
    items: [
      { to: '/ziyaret-sonuclari', labelKey: 'nav.visitResults', icon: IconFileText, permission: Permission.ReadAudits },
      { to: '/takip', labelKey: 'nav.followUp', icon: IconListChecks, permission: Permission.ReadAudits },
      { to: '/harita', labelKey: 'nav.locationMap', icon: IconMap, permission: Permission.ReadAudits },
    ],
  },
  {
    id: 'definitions',
    labelKey: 'app.definitions',
    items: [
      { to: '/ziyaret-tipleri', labelKey: 'nav.visitTypes', icon: IconMapPinned, permission: Permission.ManageDefinitions },
      { to: '/kategoriler', labelKey: 'nav.categories', icon: IconTags, permission: Permission.ManageDefinitions },
      { to: '/sorular', labelKey: 'nav.questions', icon: IconCircleHelp, permission: Permission.ManageDefinitions },
      { to: '/basliklar', labelKey: 'nav.checklistTitles', icon: IconListChecks, permission: Permission.ManageDefinitions },
      { to: '/ziyaret-skorlari', labelKey: 'nav.scoreBands', icon: IconGauge, permission: Permission.ManageDefinitions },
      { to: '/aksiyon-firmalari', labelKey: 'nav.actionCompanies', icon: IconBuilding2, permission: Permission.ManageDefinitions },
      { to: '/merkez-ekipleri', labelKey: 'nav.centralTeams', icon: IconUsers, permission: Permission.ManageDefinitions },
      { to: '/lokasyon-tipleri', labelKey: 'nav.locationTypes', icon: IconStore, permission: Permission.ManageOrganisation },
      { to: '/harita-gostergeleri', labelKey: 'nav.mapSettings', icon: IconMap, permission: Permission.ManageDefinitions },

      // Last in the group, and deliberately apart from the rest: everything above defines
      // what is in the product, this one decides how it behaves.
      { to: '/sistem-parametreleri', labelKey: 'nav.systemParameters', icon: IconSlidersHorizontal, permission: Permission.ManageDefinitions },
    ],
  },
  {
    id: 'geography',
    labelKey: 'app.geography',
    items: [
      { to: '/ulkeler', labelKey: 'nav.countries', icon: IconGlobe, permission: Permission.ManageOrganisation },
      { to: '/sehirler', labelKey: 'nav.cities', icon: IconBuilding, permission: Permission.ManageOrganisation },
      { to: '/ilceler', labelKey: 'nav.districts', icon: IconMap, permission: Permission.ManageOrganisation },
    ],
  },
  {
    id: 'organisation',
    labelKey: 'app.organisation',
    items: [
      { to: '/operasyonlar', labelKey: 'nav.operations', icon: IconBriefcase, permission: Permission.ManageOrganisation },
      { to: '/bolgeler', labelKey: 'nav.regions', icon: IconLandPlot, permission: Permission.ManageOrganisation },
      { to: '/lokasyonlar', labelKey: 'nav.locations', icon: IconMapPin, permission: Permission.ManageOrganisation },
      { to: '/mail-ayarlari', labelKey: 'nav.mailSettings', icon: IconMail, permission: Permission.ManageDefinitions },
      { to: '/kapali-gunler', labelKey: 'nav.closedDays', icon: IconCalendarOff, permission: Permission.ManageOrganisation },
    ],
  },
  {
    id: 'templates',
    labelKey: 'app.templates',
    items: [
      { to: '/sablonlar', labelKey: 'nav.templates', icon: IconLayers, permission: Permission.ManageTemplates },
    ],
  },
  {
    // Departments sits here rather than among the definition screens. It is part of the
    // chart — a position is a seat in a department — and somebody building the organisation
    // looks for it beside the rest of it, not between question categories and score bands.
    id: 'people',
    labelKey: 'app.people',
    items: [
      { to: '/kullanicilar', labelKey: 'nav.users', icon: IconUserRound, permission: Permission.ManageUsers },
      { to: '/kullanici-gruplari', labelKey: 'nav.groups', icon: IconUsers, permission: Permission.ManageUsers },
      { to: '/meslekler', labelKey: 'nav.professions', icon: IconBriefcase, permission: Permission.ManageUsers },
      { to: '/organizasyon-semasi', labelKey: 'nav.chart', icon: IconNetwork, permission: Permission.ManageUsers },
      { to: '/departmanlar', labelKey: 'nav.departments', icon: IconBuilding2, permission: Permission.ManageOrganisation },
      { to: '/roller', labelKey: 'nav.roles', icon: IconShieldCheck, permission: Permission.ManageUsers },
    ],
  },
];
